import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import apiConfig from '../../../constants/apiConfig';
import { FileScopeType } from './useGetAllFiles';

// Per-file RAG processing state from GET /dice/files/processing-status.
// 'processing' = a queue job is in flight (or the upload is recent enough that
// inline processing may still be running); 'stuck' = nothing will index the
// file without a reprocess request. Files already indexed are omitted.
export type FileProcessingState = 'processing' | 'stuck';

type ProcessingStatusResponse = {
  dataset: { id: string; state: FileProcessingState }[];
};

export const fileProcessingStatusKeys = {
  all: ['fileProcessingStatus'],
  byWorkspace: (ownerId?: string) => ['fileProcessingStatus', ownerId],
};

// Refresh while any file is unprocessed so badges resolve live (e.g. after a
// re-run, or when the worker finishes a file).
const REFETCH_INTERVAL_MS = 20_000;

/**
 * Map of notefolderId -> processing state for every not-yet-indexed file in the
 * workspace. Only fetches (and polls) while the caller reports unprocessed
 * files, so workspaces with everything indexed cost nothing.
 */
export function useFileProcessingStatus(
  ownerId?: string,
  scopeType?: FileScopeType,
  hasUnprocessedFiles?: boolean
) {
  return useQuery<Record<string, FileProcessingState>>({
    queryKey: fileProcessingStatusKeys.byWorkspace(ownerId),
    queryFn: async () => {
      const response = await axios.get<ProcessingStatusResponse>(
        `${apiConfig.apiGateway.filesBase}/processing-status`,
        {
          params: {
            workspaceId: ownerId,
            ...(scopeType ? { scopeType } : {}),
          },
        }
      );
      return Object.fromEntries(
        response.data.dataset.map(({ id, state }) => [id, state])
      );
    },
    enabled: !!ownerId && !!hasUnprocessedFiles,
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}
